import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';

import { createEntityDenormalizer } from '../../../redux/entities/normalizers';
import type {
  EntitiesMap,
  StoreEntityKey,
} from '../../../redux/entities/types';
import {
  defaultEntityMeta,
  type EntityMeta,
  updateEntityMeta,
} from '../../../redux/entities_meta/slice';
import type { EndpointResponseNormalized } from '../../../redux/middlewares/http_normalizer/types';
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from '../../../redux/redux_hooks';
import {
  fromEndpointError,
  fromError,
  type SerializableEndpointError,
  type SerializableError,
} from '../../../redux/serializable_error';
import type { AppDispatch } from '../../../redux/types';
import { statusShortcuts } from './status_shortcuts';
import type { FetchStatusType, StatusType } from './status_type';

export type UseEntityOptions<
  EntityType extends StoreEntityKey,
  Result extends EndpointResponseNormalized,
> = {
  entityType: EntityType;
  primaryId: string | undefined;
  getItem: (dispatch: AppDispatch, primaryId: string) => Promise<Result>;
};

const cacheTtlStale = 1000 * 5;

function isStale(entityMeta: EntityMeta) {
  return Date.now() - entityMeta.fetchedAt > cacheTtlStale;
}

function getPromiseCacheKey(entityType: StoreEntityKey, primaryId: string) {
  return `${entityType}:${primaryId}`;
}

const getEntitiesPromiseCache: Record<string, Promise<unknown> | undefined> =
  {};

export type UseEntityResult<EntityType extends StoreEntityKey> = {
  entity: EntitiesMap[EntityType] | undefined;
  status: StatusType;
  fetchStatus: FetchStatusType;
  error: SerializableEndpointError | SerializableError | undefined;
} & ReturnType<typeof statusShortcuts>;

export function useEntity<
  EntityType extends StoreEntityKey,
  Result extends EndpointResponseNormalized,
>({
  entityType,
  getItem,
  primaryId,
}: UseEntityOptions<EntityType, Result>): UseEntityResult<EntityType> {
  const denormalizer = useMemo(
    () => createEntityDenormalizer(entityType),
    [entityType],
  );
  // Subscribe to the denormalized entity itself (reference-stable while its
  // value is unchanged) instead of the whole entities state, so unrelated
  // entity updates don't re-render the consumer.
  const entity = useAppSelector((state) =>
    primaryId ? denormalizer(state.entities, primaryId) : undefined,
  );
  // Subscribe only to the meta fields that affect rendering. `fetchedAt` is
  // deliberately excluded: the http normalizer bumps it for every entity in
  // every response, and it only matters for the staleness check in the
  // fetch effect below, which reads it from the store imperatively.
  const { status, fetchStatus, error } = useAppSelector((state) => {
    const entityMeta = primaryId
      ? (state.entitiesMeta[entityType][primaryId] ?? defaultEntityMeta)
      : defaultEntityMeta;
    return {
      status: entityMeta.status,
      fetchStatus: entityMeta.fetchStatus,
      error: entityMeta.error,
    };
  }, shallowEqual);

  const store = useAppStore();
  const dispatch = useAppDispatch();

  const getEntity = useCallback(() => {
    if (!primaryId) {
      return;
    }
    const promiseCacheKey = getPromiseCacheKey(entityType, primaryId);
    if (getEntitiesPromiseCache[promiseCacheKey]) {
      return getEntitiesPromiseCache[promiseCacheKey];
    }
    dispatch(
      updateEntityMeta({
        entityType,
        id: primaryId,
        meta: {
          fetchStatus: 'fetching',
        },
      }),
    );
    const promise = getItem(dispatch, primaryId)
      .catch((err) => {
        if (!primaryId) {
          return;
        }
        if (err instanceof EndpointError) {
          dispatch(
            updateEntityMeta({
              entityType,
              id: primaryId,
              meta: {
                status: 'error',
                fetchStatus: 'idle',
                error: fromEndpointError(err),
              },
            }),
          );
        } else {
          dispatch(
            updateEntityMeta({
              entityType,
              id: primaryId,
              meta: {
                status: 'error',
                fetchStatus: 'idle',
                error: fromError(err),
              },
            }),
          );
        }
        throw err;
      })
      .then((result) => {
        if (!primaryId) {
          return;
        }
        dispatch(
          updateEntityMeta({
            entityType,
            id: primaryId,
            meta: {
              status: 'success',
              fetchStatus: 'idle',
              fetchedAt: Date.now(),
              error: undefined,
            },
          }),
        );
        return result;
      })
      .finally(() => {
        delete getEntitiesPromiseCache[promiseCacheKey];
      });

    getEntitiesPromiseCache[promiseCacheKey] = promise;

    return promise;
  }, [primaryId, getItem, entityType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount and when fetch function changes
  useEffect(() => {
    const entityMeta = primaryId
      ? (store.getState().entitiesMeta[entityType][primaryId] ??
        defaultEntityMeta)
      : defaultEntityMeta;
    if (!isStale(entityMeta)) {
      return;
    }
    getEntity();
  }, [getEntity]);

  return {
    entity,
    status,
    fetchStatus,
    error,
    ...statusShortcuts(status, fetchStatus),
  };
}
